// Adsterra display ads on the network's sites, each with its own units (AD_SITES: Adsterra registers units per domain,
// so every site has its own keys and script host). Shared by the Worker (worker/src/ads.js renders the slots and serves
// /ads/frame) and the browser (site/js/app.js and the public meeting page, dashboard-src/ads/AdSlot.tsx, load them).
//
// Banners and native units: each loads alone in its own sandboxed iframe (/ads/frame?unit=…), because the banner
// code reads one global (atOptions) when its script loads, and so the ad's scripts never run in our page: in production
// the frame is served from the site's bare domain (chicagopolitics.org, austinpolitics.org, …), a different origin from
// the site (www.…), so it can't reach the page's DOM, storage or cookies (all host-only on www).
//
// Adsterra can be paused on a site without losing its codes: "adsterra": false in its AD_SITES entry (Chicago's, while
// Google AdSense reviews chicagopolitics.org). Its pages then load no Adsterra frame and /ads/frame answers 404; the slots
// stay for direct campaigns and the house promo, and AdSense Auto ads are untouched. Deleting the line turns it back on
// (docs/MULTI_CITY.md "Ads and analytics").
//
// Each slot stays collapsed (no height, label or spacing, site/css/style.css .ad-slot) until something fills it
// (data-ad-filled), and its frame is made only when the slot nears the viewport. No slot loads on a no-ads page or for a
// reader in the EU/EEA, the UK or Switzerland (site/js/ad-rules.js).

// Direct ads (citypolitics.org, site/js/admarket.js) come first: when an approved, paid campaign in its dates fits the
// site, the page type and the slot's size, the slot shows its creative in our own markup (an image link or a native
// card, no advertiser code) instead of the Adsterra frame; otherwise Adsterra, as before (directFor, loadSlot).

// The house promo ("Your ad could be here", site/js/house-ad.js): a house slot (data-ad-house, one in-article slot and one
// data-page slot: HOUSE_SLOTS) shows it where no direct campaign fits, instead of Adsterra; and any slot Adsterra left
// empty, or a size the site has no unit for, shows it instead of folding away (promoFallback).

import { noAds, syncAds } from './ad-rules.js';   // the no-ads pages and countries, and AdSense Auto ads
import { pageFillTracker, slotTypeOf } from './ad-fill.js';   // whether each slot filled, counted without identifiers
import { abVariant, directAdHtml, clickHref, pageContext, excludedPath, VIEWABLE } from './admarket.js';
import { HOUSE_SLOTS, isHouseSlot } from './house-ad.js';

export { HOUSE_SLOTS, isHouseSlot };

export const AD_LABEL = 'Advertisement';

// The sizes we use. kind "native": the Native Banner (its script fills #container-<key>); kind "banner": an iframe
// banner of that size.
export const AD_SIZES = {
  native: { kind: 'native', width: null, height: 300 },
  '728x90': { kind: 'banner', width: 728, height: 90 },
  '468x60': { kind: 'banner', width: 468, height: 60 },
  '320x50': { kind: 'banner', width: 320, height: 50 },
  '300x250': { kind: 'banner', width: 300, height: 250 },
  '160x600': { kind: 'banner', width: 160, height: 600 },
  '160x300': { kind: 'banner', width: 160, height: 300 },
};

// Each site's Adsterra units, by city slug (cities/<slug>.json): its bare domain (where its frames are served), the
// script host its codes load from, and the key of each unit it has, by size.
// A site without units carries no slots; a slot that wants a size the site lacks falls back (resolveUnit).
// Every unit of a site loads from its one origin, and no key belongs to two sites (the script rejects both).
// Add or change a site's codes with scripts/adsterra-add-codes.mjs (it rewrites the block between these markers).
// adsterra-sites:start
export const AD_SITES = {
  "chicago": {
    "adsterra": false,
    "domain": "chicagopolitics.org",
    "origin": "https://bauval.org",
    "units": {
      "native": "755fea2c84fe86c0bfd84da9cabf57f1",
      "728x90": "7ab8752d775401f51ac335b237b38435",
      "468x60": "4bcb889f04922468f20d6ca3f6ebba76",
      "320x50": "8c4996309a7be7eca8b0d98fe1df7635",
      "300x250": "0944ebba2c0c914ff85821d92c4594d2",
      "160x600": "687c612058e996580fbd1ae55bffc07f",
      "160x300": "f9c91dc055057f5b7b2bdfb37f6c6f1c"
    }
  },
  "nyc": {
    "domain": "nycpolitics.org",
    "origin": "https://bicea.org",
    "units": {
      "native": "9d174a90b956626b7dba3fbabe91e63e",
      "728x90": "ab55942552c9d6d9b63063cc309c6a30",
      "468x60": "8a5a633343f4fb8508833b762f38e2c1",
      "320x50": "7582ebd767bc336ac7866748327fd102",
      "300x250": "b4caf423bc60b459ad9d49b294f0643f",
      "160x600": "ae610578abcca796ee7650e4fb9ee768",
      "160x300": "37c83d537e7eace045a00651859d94a5"
    }
  },
  "austin": {
    "domain": "austinpolitics.org",
    "origin": "https://bicea.org",
    "units": {
      "native": "61c5a59295e3ad76192f4d55e9b12a37",
      "728x90": "dda031a4e8ef786960e7608c246f0f9c",
      "468x60": "f31b737a2be259b48aa5740c7256aa86",
      "300x250": "0e179a2d82c4b5c7cf0c7e857c7e4f98",
      "160x600": "b971122f051bb5fa1708490c98a6109f",
      "160x300": "0d2d8f87133cd4e5be42e19ffe004245"
    }
  },
  "boston": {
    "domain": "bostonpolitics.org",
    "origin": "https://bicea.org",
    "units": {
      "native": "27869a312ea96c5ca22624a8a86acac0",
      "728x90": "c49a35bcefd00f31a35f21f3dbad8169",
      "468x60": "f43f3086bf5114ecd723abd2d618a851",
      "320x50": "bc5f5941ce556379e9f2808cf78b02fd",
      "300x250": "d664de9e6a68327a26c52368ff214eff",
      "160x600": "ee1e2229444207237ef7cc0d20d92cfc",
      "160x300": "db7c07ad863ebafe483a942780a52fb2"
    }
  }
};
// adsterra-sites:end

/** A site's Adsterra units as registered, paused or not: {size: {kind, key, width, height}} ({} for an unknown site). */
export function configuredUnitsOf(site = 'chicago') {
  const s = AD_SITES[site];
  if (!s?.origin) return {};
  return Object.fromEntries(Object.entries(s.units || {}).filter(([k, key]) => AD_SIZES[k] && /^[0-9a-f]{32}$/.test(key)).map(([k, key]) => [k, { ...AD_SIZES[k], key }]));
}
/** Whether a site's Adsterra units are paused ("adsterra": false): its slots show direct campaigns and the house promo only. */
export const adsterraPaused = (site) => AD_SITES[site]?.adsterra === false;
/** Whether a site loads Adsterra at all: it has units and they aren't paused. */
export const adsterraOn = (site) => !adsterraPaused(site) && Object.keys(configuredUnitsOf(site)).length > 0;
/** The units a site loads: {size: {kind, key, width, height}} ({} for a site without Adsterra, or one paused). */
export const unitsOf = (site = 'chicago') => (adsterraOn(site) ? configuredUnitsOf(site) : {});
/** Whether a site carries ad slots at all: it has Adsterra units, loaded or paused (a paused site's slots show direct
 *  campaigns and the house promo). */
export const siteHasAds = (site) => Object.keys(configuredUnitsOf(site)).length > 0;

// Chicago's units and script host, as registered (the stats script and the tests read these).
export const AD_SCRIPT_ORIGIN = AD_SITES.chicago.origin;
export const AD_UNITS = configuredUnitsOf('chicago');

const LEADERBOARDS = ['728x90', '468x60', '320x50'];

/**
 * The unit a slot asks for on a site: `want` when the site has it ("leaderboard" when it has any leaderboard size),
 * else its native unit, else null (no slot). The browser picks the leaderboard's size at load time (resolveUnit).
 */
export function slotUnit(site, want, { direct = false, house = false } = {}) {
  const u = unitsOf(site);
  if (want === 'leaderboard' ? LEADERBOARDS.some((k) => u[k]) : u[want]) return want;
  // Adsterra paused: every slot keeps its size, for a direct campaign or the house promo (else it folds away).
  if (adsterraPaused(site) && (want === 'leaderboard' || AD_SIZES[want])) return want;
  // A house slot keeps its size on every site: the house promo is drawn in it (house-ad.js PROMO_UNITS).
  if (house && (want === 'leaderboard' || AD_SIZES[want])) return want;
  if (u.native) return 'native';
  // A site without Adsterra units carries slots only while a direct campaign runs there (window.CITY.directAds): the
  // size it asked for, which folds away when no campaign fits.
  return direct && (want === 'leaderboard' || AD_SIZES[want]) ? want : null;
}

/**
 * The unit a slot loads on a site, given the room it has (`width`). A leaderboard: its size by that room (leaderboardUnit),
 * else a smaller leaderboard the site has (468x60 where 728x90 is missing), else the native unit, else a 300x250 where
 * it fits (a phone on a site without a 320x50: Austin). Any other size the site lacks: its native unit. Else null.
 */
export function resolveUnit(site, want, width = 1024) {
  const u = unitsOf(site);
  if (want === 'leaderboard') {
    const smaller = LEADERBOARDS.slice(LEADERBOARDS.indexOf(leaderboardUnit(width))).find((k) => u[k]);
    if (smaller) return smaller;
    return u.native ? 'native' : u['300x250'] && width >= 300 ? '300x250' : null;
  }
  return u[want] ? want : u.native ? 'native' : null;
}

// The responsive leaderboard: 728x90 from this width up (the slot's CSS uses the same breakpoint), 320x50 below.
export const LEADERBOARD_MIN_WIDTH = 768;

/**
 * Data pages that carry ads: a ward's page, a matter (vote/legislation) page, a meeting page. The leaderboard after the
 * first section, then (on the longer pages) more slots further down, at least a screen apart: DATA_PAGE_SLOTS.
 */
export const AD_PAGE = /^\/(?:ward\/\d{1,2}|matter\/[\w-]+|vote\/[\w-]+|meeting\/[\w-]+)$/;

/**
 * How many slots each data page carries (the app, site/js/app.js; the meeting page, dashboard-src/main.tsx; the server's
 * first render, worker/src/seo.js withDataPageAd), the most engaged pages (ward pages, then meetings and matters) the most.
 */
export const DATA_PAGE_SLOTS = { ward: 3, meeting: 2, matter: 2 };

/** Whether a page carries the data pages' slots (never a no-ads page: the election guide and candidates have none). */
export const isAdPage = (path) => AD_PAGE.test(path) && !noAds(path);
/** How many slots a page carries (the server's first render, worker/src/seo.js, and the app). */
export const adSlotsFor = (path) => (/^\/ward\//.test(path) ? DATA_PAGE_SLOTS.ward : /^\/meeting\//.test(path) ? DATA_PAGE_SLOTS.meeting : DATA_PAGE_SLOTS.matter);

/** The data pages' units in turn: the leaderboard, then a 300x250, then the leaderboard again, so no unit sits next to itself. */
export const dataPageUnit = (n) => (n % 2 === 1 ? 'leaderboard' : '300x250');

/** On My Block articles without ads: the founder's letter, in English and every translation (its rows' slugs, what-do-you-see-<lang>). */
export const NO_AD_ARTICLE = /^what-do-you-see(?:-[a-z]{2,3}(?:-[a-z0-9]+)?)?$/;

const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/**
 * One ad slot: a labeled box of reserved height that the browser fills with the unit's frame once it nears the
 * viewport (initAds). `unit`: a size of AD_SIZES, or "leaderboard" (728x90 or 320x50 by the viewport at load time).
 */
export function adSlotHtml(unit, { cls = '', id = '', house = false } = {}) {
  return `<div class="ad-slot ad-slot--${esc(unit)}${cls ? ` ${esc(cls)}` : ''}"${id ? ` id="${esc(id)}"` : ''} data-ad-unit="${esc(unit)}"${house ? ' data-ad-house="1"' : ''} role="complementary" aria-label="${AD_LABEL}">`
    + `<p class="ad-slot__label">${AD_LABEL}</p><div class="ad-slot__box"></div></div>`;
}

/** A slot on a site for the unit it wants (slotUnit: a size it lacks becomes its native unit), or '' when it has none. */
export function siteAdSlotHtml(site, want, opts = {}) {
  const unit = slotUnit(site, want, { direct: !!opts.direct, house: !!opts.house });
  return unit ? adSlotHtml(unit, opts) : '';
}

/** The two scripts of one of a site's units, for its frame (worker/src/ads.js adFrameResponse). Null for a unit it lacks. */
export function adUnitScripts(unit, site = 'chicago') {
  const u = Object.hasOwn(unitsOf(site), unit) ? unitsOf(site)[unit] : null;
  if (!u) return null;
  const origin = AD_SITES[site].origin;
  if (u.kind === 'native') {
    return { inline: '', src: `${origin}/21/${u.key}`, async: true, container: `container-${u.key}` };
  }
  const opts = { key: u.key, format: 'iframe', height: u.height, width: u.width, params: {} };
  return { inline: `atOptions = ${JSON.stringify(opts)};`, src: `${origin}/22/${u.key}`, async: false, container: null };
}

// ---------- in the browser ----------

/** The leaderboard's unit for the room it has: 728x90, else 468x60 (a narrow article column), else 320x50. */
export const leaderboardUnit = (width) => (width >= LEADERBOARD_MIN_WIDTH ? '728x90' : width >= 480 ? '468x60' : '320x50');

/**
 * Where the frame comes from: on a site's www host, its bare domain (another origin, so the frame keeps its own storage
 * but can't touch ours); anywhere else (local development), this origin, with an opaque sandbox instead.
 */
export function adFrameOrigin(loc) {
  const host = String(loc.hostname).toLowerCase();
  const s = Object.values(AD_SITES).find((x) => host === `www.${x.domain}`);
  return s ? `https://${s.domain}` : loc.origin;
}

/**
 * The site a page is on: by its host (www or bare domain), else the page's city (window.CITY, worker/src/city-site.js),
 * else Chicago (as site/js/city.js does).
 */
export function adSiteOf(loc, city = typeof window !== 'undefined' ? window.CITY : null) {
  const host = String(loc?.hostname || '').toLowerCase();
  const byHost = Object.keys(AD_SITES).find((k) => host === AD_SITES[k].domain || host === `www.${AD_SITES[k].domain}`);
  return byHost || city?.slug || 'chicago';
}

let observer = null;
let listening = false;
const frames = new Map();   // contentWindow -> iframe, for their height messages

// ---------- direct ads (citypolitics.org; the ad engine, worker/src/ad-engine.js) ----------

/** The size a direct ad gets in a slot: the leaderboard by the room it has, else the size the slot asked for. */
export const directUnitFor = (want, width) => (want === 'leaderboard' ? leaderboardUnit(width) : want);

/**
 * The engine's ad for one slot (GET /ads/serve on our own host): {c, v, unit, type, tok} or null (none fits, the
 * request failed, or 1.5 s passed). The page's type, district and article and the slot's size are sent, never the path;
 * the reader's A/B letter goes too (abVariant). Location targeting is matched by the engine on the request itself.
 */
let lastView = null;
const pageViewKey = (loc) => `${loc?.pathname || ''}${loc?.search || ''}`;
export async function directFor(slot, site, width, loc = location, fetcher = typeof fetch !== 'undefined' ? fetch : null, { house = false, retry = false } = {}) {
  if (!fetcher || excludedPath(loc.pathname)) return null;
  const ctx = pageContext(loc.pathname, { article: !!slot.closest?.('.omb-ad') });
  const unit = directUnitFor(slot.dataset.adUnit, width);
  // house: the engine answers with the house promo when no direct campaign fits (ad-engine.js promoPick).
  // pv=1: the first slot this page view asks for (the engine counts page views from it, in aggregate: ad-audience.js);
  // r=1: a slot asking again (the house promo after Adsterra), not counted twice.
  const view = pageViewKey(loc);
  const first = !retry && view !== lastView;
  if (first) lastView = view;
  const q = new URLSearchParams({ t: ctx.type, u: unit, v: abVariant(), ...(ctx.district ? { d: String(ctx.district) } : {}), ...(ctx.article ? { a: ctx.article } : {}), ...(house ? { h: '1' } : {}), ...(first ? { pv: '1' } : {}), ...(retry ? { r: '1' } : {}) });
  const timeout = new Promise((ok) => setTimeout(() => ok(null), 1500));
  const r = await Promise.race([fetcher(`/ads/serve?${q}`, { credentials: 'omit' }).then((x) => (x.ok ? x.json() : null)).catch(() => null), timeout]);
  return r?.ad ? { c: r.ad, v: r.v === 'b' ? 'b' : 'a', unit: r.unit || unit, type: ctx.type, tok: r.tok || '' } : null;
}

// Viewable impressions go out batched with sendBeacon (POST /ads/e): each one the served ad's signed token, nothing else.
let events = [];
let eventTimer = null;
function sendEvents() {
  clearTimeout(eventTimer); eventTimer = null;
  if (!events.length) return;
  const body = JSON.stringify({ events: events.splice(0, 20) });
  try { navigator.sendBeacon?.('/ads/e', new Blob([body], { type: 'application/json' })); } catch { /* ignore */ }
  if (events.length) sendEvents();
}
function queueEvent(e) {
  events.push(e);
  if (events.length >= 20) sendEvents(); else if (!eventTimer) eventTimer = setTimeout(sendEvents, 2000);
}
let eventsListening = false;

/**
 * Counts a viewable impression once at least half the ad has been on screen for a second (VIEWABLE), once per slot.
 * `IO` is the IntersectionObserver (the tests pass their own).
 */
export function watchViewable(el, onViewable, { IO = typeof IntersectionObserver !== 'undefined' ? IntersectionObserver : null, setT = setTimeout, clearT = clearTimeout } = {}) {
  if (!IO) return null;
  let timer = null, done = false;
  const io = new IO((entries) => {
    for (const e of entries) {
      if (done) return;
      if (e.isIntersecting && e.intersectionRatio >= VIEWABLE.ratio) { if (!timer) timer = setT(() => { done = true; io.disconnect(); onViewable(); }, VIEWABLE.ms); }
      else if (timer) { clearT(timer); timer = null; }
    }
  }, { threshold: [0, VIEWABLE.ratio, 1] });
  io.observe(el);
  return io;
}

/** Draws the engine's ad in a slot and watches for its viewable impression. */
function showDirect(slot, box, d) {
  const creative = d.c.creatives?.[d.v] || d.c.creatives?.a;
  const markup = creative ? directAdHtml(d.c, creative, { unit: d.unit, href: clickHref(d.c, d.v, d.unit, d.type, d.tok) }) : '';
  if (!markup) return false;
  box.innerHTML = markup;
  slot.dataset.adShown = d.c.house ? 'house' : 'direct';
  slot.dataset.adFilled = '1';   // our own markup: shown at once
  slot.hidden = false;
  slot.closest('.omb-ad')?.removeAttribute('hidden');
  slot.dataset.adDirect = d.c.id;
  if (!eventsListening) { eventsListening = true; addEventListener('pagehide', sendEvents); addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') sendEvents(); }); }
  if (d.tok) watchViewable(box, () => queueEvent({ e: 'view', tok: d.tok }));
  return true;
}

const fold = (slot) => { slot.hidden = true; slot.closest('.omb-ad')?.setAttribute('hidden', ''); };
const slotWidth = (slot) => Math.min(window.innerWidth, slot.clientWidth || slot.parentElement?.clientWidth || window.innerWidth);

/**
 * A slot nothing filled (Adsterra left it empty, or the site has no unit for its size): the house promo instead, else
 * it folds away. Once per slot.
 */
export async function promoFallback(slot) {
  const box = slot.querySelector('.ad-slot__box');
  if (!box || slot.dataset.adFallback) { fold(slot); return false; }
  slot.dataset.adFallback = '1';
  const d = await directFor(slot, adSiteOf(location), slotWidth(slot), location, undefined, { house: true, retry: !!slot.dataset.adLoaded }).catch(() => null);
  box.replaceChildren();
  box.style.removeProperty('min-height');
  if (d && showDirect(slot, box, d)) return true;
  fold(slot);
  return false;
}

async function loadSlot(slot) {
  if (slot.dataset.adLoaded) return;
  slot.dataset.adLoaded = '1';
  const want = slot.dataset.adUnit;
  const site = adSiteOf(location);
  const width = slotWidth(slot);
  const box = slot.querySelector('.ad-slot__box');
  // Direct campaigns first; in a house slot the house promo after them; else Adsterra (or the request failed).
  const house = slot.dataset.adHouse === '1';
  const direct = box ? await directFor(slot, site, width, location, undefined, { house }).catch(() => null) : null;
  if (direct && showDirect(slot, box, direct)) return;
  const unit = resolveUnit(site, want, width);
  const u = unit ? unitsOf(site)[unit] : null;
  // A slot this site has no unit for: the house promo, else it folds away.
  if (!u) { if (house) fold(slot); else void promoFallback(slot); return; }
  if (!box) return;
  // A slot that fell back to another unit (the native unit, or a phone's 300x250) gets that unit's room.
  if (unit !== want && !LEADERBOARDS.includes(unit)) box.style.setProperty('min-height', `${u.height}px`);
  const origin = adFrameOrigin(location);
  const crossOrigin = origin !== location.origin;
  const f = document.createElement('iframe');
  f.src = `${origin}/ads/frame?unit=${encodeURIComponent(unit)}`;
  f.title = AD_LABEL;
  f.className = 'ad-slot__frame';
  // Scripts run, and a click opens the advertiser in a new, unsandboxed tab; nothing navigates our page. Same-origin
  // privileges only when the frame is on the other origin (they then cover its own storage, never ours).
  // Pop-ups and pop-unders: allow-popups stays, or a banner's click couldn't open the advertiser at all (there is no
  // "pop-ups on a click only" sandbox token), and so does allow-popups-to-escape-sandbox, or the advertiser's page would
  // open with our sandbox (no forms, no navigation) and break. What stops a pop is inside the frame (worker/src/ads.js
  // POPUP_GUARD): a new window only for a real click in the ad, one per click, and nothing pushed behind our page. The
  // browser's own blocker already refuses a window without a click.
  f.setAttribute('sandbox', `allow-scripts allow-popups allow-popups-to-escape-sandbox${crossOrigin ? ' allow-same-origin' : ''}`);
  f.setAttribute('scrolling', 'no');
  f.referrerPolicy = 'strict-origin-when-cross-origin';
  if (u.width) f.width = String(u.width);
  f.height = String(u.height);
  slot.dataset.adShown = unit;
  box.append(f);
  frames.set(f.contentWindow, f);
  // Ad fill (ad-fill.js): this slot is classified once, filled, empty or noreport, and counted by unit and page type.
  pageFillTracker((frame) => frame.isConnected)?.created(f, { unit, type: slotTypeOf(location.pathname, { article: !!slot.closest('.omb-ad') }) });
}

// Each frame reports its content height (a native unit's box grows to fit it, up to a limit) and, twice after it loads,
// whether anything filled it: a slot still empty at the second report folds away rather than stay an empty grey box.
const reports = new WeakMap();
function onFrameMessage(e) {
  const f = frames.get(e.source);
  if (!f) return;
  const slot = f.closest('[data-ad-shown]');
  if ('adFrameFilled' in (e.data || {})) {
    const r = reports.get(f) || { n: 0, filled: false };
    r.n += 1; r.filled ||= !!e.data.adFrameFilled;
    reports.set(f, r);
    if (r.filled && slot) slot.dataset.adFilled = '1';   // the slot opens (style.css .ad-slot[data-ad-filled])
    pageFillTracker((frame) => frame.isConnected)?.report(f, !!e.data.adFrameFilled);
    // Adsterra left it empty: the house promo in its place (or the slot folds away).
    if (r.n >= 2 && !r.filled && slot && !slot.dataset.adFallback) { frames.delete(e.source); void promoFallback(slot); }
    return;
  }
  const h = Number(e.data?.adFrameHeight);
  if (!Number.isFinite(h) || slot?.dataset.adShown !== 'native') return;
  const px = Math.max(AD_SIZES.native.height, Math.min(Math.ceil(h), 900));
  f.height = String(px);
  f.closest('.ad-slot__box')?.style.setProperty('min-height', `${px}px`);
}

/**
 * Fill the ad slots under `root` as they near the viewport (about one screen ahead). Safe to call again after a redraw.
 * Also the page's ad check (ad-rules.js syncAds): on a no-ads page, or for a reader in the EU/EEA, the UK or Switzerland,
 * nothing loads; elsewhere AdSense Auto ads load too.
 */
export function initAds(root = document) {
  if (typeof window === 'undefined' || !root?.querySelectorAll) return;
  syncAds(location.pathname).then((ok) => { if (ok && (root === document || root.isConnected)) fillSlots(root); }, () => {});
}

function fillSlots(root) {
  const slots = [...root.querySelectorAll('[data-ad-unit]:not([data-ad-loaded])')];
  if (!slots.length) return;
  if (!listening) { listening = true; window.addEventListener('message', onFrameMessage); }
  if (!('IntersectionObserver' in window)) { slots.forEach(loadSlot); return; }
  if (!observer) {
    observer = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) { observer.unobserve(e.target); loadSlot(e.target); }
    }, { rootMargin: '600px 0px' });
  }
  for (const [w, f] of frames) if (!f.isConnected) frames.delete(w);
  slots.forEach((s) => observer.observe(s));
}
